// @ts-nocheck
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import Download from "@store/Library/Components/Toolkit/Icons/Download";
import useHasDocuments from "@store/Framework/Common/Hooks/Products/useHasDocuments";
import { getBuiltQueryString, QueryString } from "@store/Framework/Common/Utilities/getBuildQueryString";
import getStickyHeaderHeight from "@store/Framework/Common/Utilities/getStickyHeaderHeight";
import { linkStyles } from "@store/Themes/common/mixins/link";
import scrollToId from "@toolkit/Framework/Common/Utilities/scrollToId";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { FC } from "react";
import styled, { css } from "styled-components";

const enum fields {
    variant = "variant",
    label = "label",
    link = "link",
    topic = "topic",
    includeProductNumber = "includeProductNumber",
    alignment = "alignment",
    customCSS = "customCSS",
    showDownloadAnchor = "showDownloadAnchor",
    downloadLinkLabel = "downloadLinkLabel",
    downloadLinkAnchor = "downloadLinkAnchor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.topic]: string;
        [fields.includeProductNumber]: boolean;
        [fields.alignment]: string;
        [fields.customCSS]: string;
        [fields.showDownloadAnchor]: boolean;
        [fields.downloadLinkLabel]: string;
        [fields.downloadLinkAnchor]: string;
    };
}

export interface ButtonStyles {
    wrapperStyles?: InjectableCss;
    downloadLink?: LinkPresentationProps;
}

const buttonStyles: ButtonStyles = {
    wrapperStyles: {
        css: css`
            margin-top: ${pxToRem(16)};
            padding-top: ${pxToRem(16)};
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(16)};
        `,
    },
    downloadLink: {
        css: linkStyles("text.linkAlt", "text.linkAltHover", false, true),
        icon: {
            iconProps: {
                src: Download,
            },
            position: "right",
        },
    },
};

export const CTAButton: FC<OwnProps> = ({ fields }) => {
    const history = useHistory();
    const link = useGetLink(fields.link);
    const { isInShell } = useShellContext();
    const hasDocuments = useHasDocuments();
    const { product } = React.useContext(ProductContext);
    const queryStrings: QueryString[] = [];

    if (fields.topic) {
        queryStrings.push({ topic: fields.topic });
    }

    if (fields.includeProductNumber) {
        queryStrings.push({ genericProductNumber: product.productNumber });
    }

    const onClick = () => {
        if (!link.url) {
            return;
        }

        const _constructedLink = getBuiltQueryString(link.url, queryStrings);

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            window.location.href = _constructedLink || link.url;
        } else {
            history.push(_constructedLink || link.url);
        }
    };

    const styles = buttonStyles;

    return (
        <ContentWrapper alignment={fields.alignment} {...styles.wrapperStyles} className="wrapper">
            <Button variant={fields.variant} onClick={onClick} className="button" sizeVariant="large">
                {fields.label}
            </Button>
            {hasDocuments && fields.showDownloadAnchor && (
                <Link
                    onClick={() =>
                        scrollToId(fields.downloadLinkAnchor, { padding: getStickyHeaderHeight(), smooth: true })
                    }
                    {...styles.downloadLink}
                >
                    {fields.downloadLinkLabel}
                </Link>
            )}
        </ContentWrapper>
    );
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    align-items: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: CTAButton,
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        icon: "rectangle-wide",
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                allowUrls: () => false,
                allowCategories: () => false,
                fieldType: "General",
            },
            {
                name: fields.topic,
                displayName: "Contact Us form Topic in Query String",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.includeProductNumber,
                displayName: "Include Part Number in Contact Us Form?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                variant: "toggle",
                fieldType: "Translatable",
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.showDownloadAnchor,
                displayName: "Show Document Link",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.downloadLinkLabel,
                displayName: "Download Link Label",
                editorTemplate: "TextField",
                defaultValue: "Download Sell Sheet",
                fieldType: "Translatable",
                isVisible: x => x.fields.showDownloadAnchor,
            },
            {
                name: fields.downloadLinkAnchor,
                editorTemplate: "TextField",
                defaultValue: "Documents",
                fieldType: "Translatable",
                isVisible: x => x.fields.showDownloadAnchor,
            },
        ],
    },
};

export default widgetModule;
